import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getActiveDrawer } from '../../redux/modules/common/selectors';
import { commonActionCreators } from '../../redux/modules/common';
import { appointmentActionCreators } from '../../redux/modules/appointment';
import { selectProviderSchedule } from '../../redux/modules/schedule/selectors';
import { MemberActions } from '../../packages/ui/molecules/member-actions';
import { Drawer, drawerType } from '../../packages/ui/organisms/drawer';
import Priorities from '../../pages/provider/priorities';
import AddSchedule from '../../pages/admin/appointments/add-schedule';
import AskMeAnything from '../../components/ask-me-anything';
import Chats from '../../pages/provider/sessions/video-call-screen/components/chat-drawer';
import { MEMBER_ACTIONS } from '../../packages/ui/templates/sessions/sessions.constants';
import { useStyles } from './MemberDrawerController.styles';

/**
 * Controller component that handles all member action drawers and logic in one place
 * This prevents code duplication across the app and centralizes drawer management
 */
const MemberDrawerController = ({
  appointment,
  careTeamList = [],
  expanded = true,
  renderButtons = true,
  chatIconGlyph = 'message',
  isInPersonSessionCall = false,
  className,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const activeDrawer = useSelector(getActiveDrawer);
  const providerSchedule = useSelector(selectProviderSchedule);

  const handleOpenDrawer = type => {
    dispatch(commonActionCreators.openDrawer(type));
  };

  const handleCloseDrawer = () => {
    dispatch(commonActionCreators.closeDrawer());
  };

  const handleScheduleSubmit = payload => {
    dispatch(
      appointmentActionCreators.createAppointment({
        data: { ...payload, timezone: providerSchedule?.timezone },
        callback: isSucceed => {
          if (isSucceed) {
            handleCloseDrawer();
            dispatch(commonActionCreators.fetchNextAppointment(true));
          }
        },
      }),
    );
  };

  return (
    <>
      {/* Only render the buttons if requested */}
      {renderButtons && (
        <MemberActions
          onMemberActionClick={handleOpenDrawer}
          expanded={expanded}
          chatIconGlyph={chatIconGlyph}
          className={className}
        />
      )}

      {/* These drawers will appear anywhere in the app */}
      {activeDrawer === MEMBER_ACTIONS.PROFILE && (
        <Drawer
          stickyHeader
          title="Member Profile"
          open={activeDrawer === MEMBER_ACTIONS.PROFILE}
          onClose={handleCloseDrawer}
          variant={drawerType.FORM}
          footer
          className={classes.drawer}
        >
          <Priorities
            hideNotes
            currentAppointment={appointment}
            isInPersonSessionCall={isInPersonSessionCall}
            isSession
            handleMemberActionClick={handleOpenDrawer}
          />
        </Drawer>
      )}

      {activeDrawer === MEMBER_ACTIONS.SCHEDULE && (
        <AddSchedule
          isOpen={activeDrawer === MEMBER_ACTIONS.SCHEDULE}
          onClose={handleCloseDrawer}
          onSubmit={handleScheduleSubmit}
          memberId={appointment?.patientId || appointment?.participantId}
          appointment={appointment}
          isVideoSession={false}
        />
      )}

      {activeDrawer === MEMBER_ACTIONS.AMA && (
        <AskMeAnything
          useDrawer
          isOpen={activeDrawer === MEMBER_ACTIONS.AMA}
          onClose={handleCloseDrawer}
          memberId={appointment?.patientId || appointment?.participantId}
        />
      )}

      {activeDrawer === MEMBER_ACTIONS.CHAT && (
        <Drawer
          open={activeDrawer === MEMBER_ACTIONS.CHAT}
          onClose={handleCloseDrawer}
          variant={drawerType.NORMAL}
          className={classes.drawer}
        >
          <Chats careTeamList={careTeamList} appointment={appointment} onClose={handleCloseDrawer} />
        </Drawer>
      )}
    </>
  );
};

export default MemberDrawerController;
